import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, UserCircle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdminSidebar from '@/components/layout/AdminSidebar';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const BATTING_STYLES = ['Right-hand', 'Left-hand', 'Unknown'];
const BOWLING_STYLES = ['Fast', 'Medium', 'Spin', 'None', 'Unknown'];
const PLAYER_ROLES = ['Batsman', 'Bowler', 'All-rounder', 'Wicketkeeper'];

export default function PlayerManager({ user, setUser }) {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTeam, setFilterTeam] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [deletingPlayer, setDeletingPlayer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    team_id: '',
    batting_style: 'Unknown',
    bowling_style: 'Unknown',
    player_role: 'Batsman'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchPlayers();
  }, [filterTeam]);

  const fetchData = async () => {
    try {
      const [playersRes, teamsRes, leaguesRes] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/teams`),
        axios.get(`${API}/leagues`)
      ]);
      setPlayers(playersRes.data);
      setTeams(teamsRes.data);
      setLeagues(leaguesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const params = filterTeam !== 'all' ? { team_id: filterTeam } : {};
      const response = await axios.get(`${API}/players`, { params });
      setPlayers(response.data);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const handleOpenDialog = (player = null) => {
    if (player) {
      setEditingPlayer(player);
      setFormData({
        name: player.name,
        team_id: player.team_id,
        batting_style: player.batting_style,
        bowling_style: player.bowling_style,
        player_role: player.player_role
      });
    } else {
      setEditingPlayer(null);
      setFormData({
        name: '',
        team_id: filterTeam !== 'all' ? filterTeam : '',
        batting_style: 'Unknown',
        bowling_style: 'Unknown',
        player_role: 'Batsman'
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingPlayer) {
        const { team_id, ...updateData } = formData;
        await axios.put(`${API}/players/${editingPlayer.player_id}`, updateData);
        toast.success('Player updated successfully');
      } else {
        await axios.post(`${API}/players`, formData);
        toast.success('Player added successfully');
      }
      setDialogOpen(false);
      fetchPlayers();
    } catch (error) {
      console.error('Error saving player:', error);
      toast.error(error.response?.data?.detail || 'Failed to save player');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingPlayer) return;

    try {
      await axios.delete(`${API}/players/${deletingPlayer.player_id}`);
      toast.success('Player removed successfully');
      setDeleteDialogOpen(false);
      setDeletingPlayer(null);
      fetchPlayers();
    } catch (error) {
      console.error('Error deleting player:', error);
      toast.error('Failed to delete player');
    }
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.team_id === teamId);
    return team?.name || 'Unknown Team';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Batsman': return 'bg-blue-100 text-blue-700';
      case 'Bowler': return 'bg-green-100 text-green-700';
      case 'All-rounder': return 'bg-purple-100 text-purple-700';
      case 'Wicketkeeper': return 'bg-orange-100 text-orange-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminSidebar user={user} setUser={setUser} />
      
      <div className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Players</h1>
              <p className="text-slate-500 mt-1">Manage player rosters</p>
            </div>
            <Button 
              onClick={() => handleOpenDialog()}
              className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-xl"
              disabled={teams.length === 0}
              data-testid="create-player-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Player
            </Button>
          </div>

          {/* Filter */}
          <div className="mb-6 max-w-xs">
            <Select value={filterTeam} onValueChange={setFilterTeam}>
              <SelectTrigger className="rounded-xl" data-testid="filter-team">
                <SelectValue placeholder="Filter by team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(team => (
                  <SelectItem key={team.team_id} value={team.team_id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Players Grid */}
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="skeleton h-32 rounded-2xl"></div>
              ))}
            </div>
          ) : teams.length === 0 ? (
            <div className="empty-state glass-card rounded-2xl">
              <div className="empty-state-icon">
                <Users className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">Create a team first</h3>
              <p className="text-slate-500 mt-2">You need at least one team before adding players</p>
            </div>
          ) : players.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {players.map((player) => (
                <Card key={player.player_id} className="glass-card rounded-2xl overflow-hidden" data-testid={`player-${player.player_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-200 flex items-center justify-center text-slate-500 font-semibold flex-shrink-0">
                        {player.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 truncate">{player.name}</h3>
                        <p className="text-xs text-slate-500 truncate">{getTeamName(player.team_id)}</p>
                      </div>
                    </div>
                    
                    <Badge className={`${getRoleBadgeColor(player.player_role)} mb-3`}>
                      {player.player_role}
                    </Badge>
                    
                    <div className="text-xs text-slate-500 space-y-0.5 mb-3">
                      <p>Bat: {player.batting_style}</p>
                      <p>Bowl: {player.bowling_style}</p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 rounded-lg text-xs"
                        onClick={() => handleOpenDialog(player)}
                        data-testid={`edit-player-${player.player_id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        onClick={() => {
                          setDeletingPlayer(player);
                          setDeleteDialogOpen(true);
                        }}
                        data-testid={`delete-player-${player.player_id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="empty-state glass-card rounded-2xl">
              <div className="empty-state-icon">
                <UserCircle className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">No players found</h3>
              <p className="text-slate-500 mt-2">Add your first player to get started</p>
              <Button 
                onClick={() => handleOpenDialog()}
                className="mt-4 bg-emerald-900 hover:bg-emerald-800 text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Player
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPlayer ? 'Edit Player' : 'Add New Player'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {!editingPlayer && (
                <div className="space-y-2">
                  <Label htmlFor="team">Team *</Label>
                  <Select 
                    value={formData.team_id} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, team_id: value }))}
                  >
                    <SelectTrigger data-testid="player-team-select">
                      <SelectValue placeholder="Select a team" />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map(team => (
                        <SelectItem key={team.team_id} value={team.team_id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="name">Player Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Virat Kohli"
                  required
                  data-testid="player-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="player_role">Role *</Label>
                <Select 
                  value={formData.player_role} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, player_role: value }))}
                >
                  <SelectTrigger data-testid="player-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAYER_ROLES.map(role => (
                      <SelectItem key={role} value={role}>{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="batting_style">Batting Style</Label>
                  <Select 
                    value={formData.batting_style} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, batting_style: value }))}
                  >
                    <SelectTrigger data-testid="player-batting-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BATTING_STYLES.map(style => (
                        <SelectItem key={style} value={style}>{style}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bowling_style">Bowling Style</Label>
                  <Select 
                    value={formData.bowling_style} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, bowling_style: value }))}
                  >
                    <SelectTrigger data-testid="player-bowling-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BOWLING_STYLES.map(style => (
                        <SelectItem key={style} value={style}>{style}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-900 hover:bg-emerald-800"
                disabled={submitting || (!editingPlayer && !formData.team_id)}
                data-testid="save-player-btn"
              >
                {submitting ? 'Saving...' : (editingPlayer ? 'Update' : 'Add')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Player?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{deletingPlayer?.name}" from the team. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-player"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
